import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Form, Button, Alert, Card, Spinner, Row, Col } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  fetchCategories,
  createCategory,
  updateCategory,
  CONTENT_TYPES,
  type ContentType,
} from '../redux/slices/categoriesSlice';

const PRESET_COLORS = [
  '#00579a', '#1890ff', '#13c2c2', '#52c41a',
  '#fa8c16', '#722ed1', '#eb2f96', '#cf1322',
  '#595959', '#8c8c8c',
];

const CategoryForm: React.FC = () => {
  const navigate   = useNavigate();
  const dispatch   = useAppDispatch();
  const { id }     = useParams<{ id: string }>();
  const isEdit     = !!id;

  const { items: categories, loading: storeLoading } = useAppSelector(s => s.categories);

  const [formData, setFormData] = useState({
    name:        '',
    description: '',
    color:       '#00579a',
  });
  const [selectedTypes, setSelectedTypes] = useState<ContentType[]>([]);
  const [submitting, setSubmitting]       = useState(false);
  const [error, setError]                 = useState('');
  const [initialLoading, setInitialLoading] = useState(false);

  // Load existing category on edit
  useEffect(() => {
    if (!isEdit) return;

    const fromStore = categories.find(c => c.id === id);
    if (fromStore) {
      populate(fromStore);
    } else {
      setInitialLoading(true);
      dispatch(fetchCategories({})).then(action => {
        if (fetchCategories.fulfilled.match(action)) {
          const found = (action.payload.data as any[]).find((c: any) => c.id === id);
          if (found) populate(found);
          else setError('Category not found.');
        }
        setInitialLoading(false);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const populate = (cat: any) => {
    setFormData({
      name:        cat.name,
      description: cat.description || '',
      color:       cat.color || '#00579a',
    });
    const types = cat.applies_to
      .split(',')
      .map((s: string) => s.trim())
      .filter(Boolean) as ContentType[];
    setSelectedTypes(types);
  };

  const handleTypeToggle = (type: ContentType) => {
    setSelectedTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Name is required.');
      return;
    }
    if (selectedTypes.length === 0) {
      setError('Select at least one content type.');
      return;
    }

    setSubmitting(true);

    const payload = {
      name:        formData.name.trim(),
      description: formData.description.trim() || undefined,
      applies_to:  selectedTypes,
      color:       formData.color,
    };

    const result = isEdit
      ? await dispatch(updateCategory({ id: id!, data: payload }))
      : await dispatch(createCategory(payload));

    if (
      createCategory.rejected.match(result) ||
      updateCategory.rejected.match(result)
    ) {
      setError(result.payload as string);
      setSubmitting(false);
      return;
    }

    navigate('/categories');
  };

  if (initialLoading || (isEdit && storeLoading && categories.length === 0)) {
    return (
      <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingTop: '2rem' }}>
        <Container>
          <div className="text-center" style={{ padding: '60px 0' }}>
            <Spinner animation="border" style={{ color: '#00579a', width: '40px', height: '40px' }} />
            <p style={{ marginTop: '16px', color: '#8c8c8c' }}>Loading category...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingTop: '2rem', paddingBottom: '2rem' }}>
      <Container>
        <div className="mb-4">
          <h1 style={{ color: '#00579a', fontWeight: 600, fontSize: '28px', margin: 0 }}>
            {isEdit ? 'Edit Category' : 'New Category'}
          </h1>
        </div>

        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setError('')}
            style={{ borderRadius: '6px', border: '1px solid #ffccc7', backgroundColor: '#fff2f0' }}
          >
            {error}
          </Alert>
        )}

        <Card style={{ border: 'none', borderRadius: '8px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
          <Card.Body style={{ padding: '28px' }}>
            <Form onSubmit={handleSubmit}>

              {/* Name */}
              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Construction, Technology, General"
                  required
                  style={inputStyle}
                />
                {formData.name && (
                  <Form.Text style={{ color: '#8c8c8c', fontSize: '12px' }}>
                    Slug: <code>{slugPreview(formData.name)}</code>
                  </Form.Text>
                )}
              </Form.Group>

              {/* Description */}
              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.description}
                  onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                  placeholder="Optional short description"
                  style={{ ...inputStyle, resize: 'none' }}
                />
              </Form.Group>

              {/* Applies To */}
              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Applies To *</Form.Label>
                <p style={{ fontSize: '13px', color: '#8c8c8c', marginBottom: '12px' }}>
                  Select which content types can use this category
                </p>
                <Row className="g-2">
                  {CONTENT_TYPES.map(ct => {
                    const active = selectedTypes.includes(ct.value);
                    return (
                      <Col xs={6} sm={4} md={3} key={ct.value}>
                        <div
                          onClick={() => handleTypeToggle(ct.value)}
                          style={{
                            padding: '10px 14px',
                            borderRadius: '6px',
                            border: `1px solid ${active ? '#00579a' : '#d9d9d9'}`,
                            backgroundColor: active ? '#e6f4ff' : 'white',
                            cursor: 'pointer',
                            userSelect: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.15s ease',
                            fontSize: '13px',
                            fontWeight: active ? 600 : 400,
                            color: active ? '#00579a' : '#595959',
                          }}
                        >
                          <span style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '3px',
                            border: `2px solid ${active ? '#00579a' : '#d9d9d9'}`,
                            backgroundColor: active ? '#00579a' : 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            {active && (
                              <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </span>
                          {ct.label}
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Form.Group>

              {/* Color */}
              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Color</Form.Label>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  {/* Preset swatches */}
                  <div className="d-flex flex-wrap gap-2">
                    {PRESET_COLORS.map(c => (
                      <button
                        key={c}
                        type="button"
                        title={c}
                        onClick={() => setFormData(p => ({ ...p, color: c }))}
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          backgroundColor: c,
                          border: formData.color === c
                            ? '3px solid #262626'
                            : '2px solid transparent',
                          cursor: 'pointer',
                          padding: 0,
                          outline: 'none',
                          boxSizing: 'border-box',
                          transition: 'border 0.15s ease',
                        }}
                      />
                    ))}
                  </div>
                  {/* Custom picker */}
                  <div className="d-flex align-items-center gap-2">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={e => setFormData(p => ({ ...p, color: e.target.value }))}
                      style={{
                        width: '36px',
                        height: '36px',
                        padding: '2px',
                        borderRadius: '6px',
                        border: '1px solid #d9d9d9',
                        cursor: 'pointer',
                        backgroundColor: 'white',
                      }}
                    />
                    <span style={{ fontSize: '13px', color: '#595959' }}>{formData.color}</span>
                    {/* Preview badge */}
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: '4px',
                      backgroundColor: formData.color + '20',
                      color: formData.color,
                      fontSize: '12px',
                      fontWeight: 600,
                      border: `1px solid ${formData.color}40`,
                    }}>
                      {formData.name || 'Preview'}
                    </span>
                  </div>
                </div>
              </Form.Group>

              {/* Actions */}
              <div className="d-flex gap-2 pt-2">
                <Button
                  type="submit"
                  disabled={submitting}
                  style={{
                    backgroundColor: '#00579a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 24px',
                    fontWeight: 500,
                    boxShadow: '0 2px 4px rgba(0,87,154,0.2)',
                  }}
                >
                  {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Category'}
                </Button>
                <Button
                  type="button"
                  onClick={() => navigate('/categories')}
                  disabled={submitting}
                  style={{
                    backgroundColor: 'white',
                    color: '#595959',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '10px 24px',
                    fontWeight: 500,
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

// ─── Helpers & styles ──────────────────────────────────────────────────────

const slugPreview = (name: string) =>
  name.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');

const labelStyle: React.CSSProperties = {
  fontWeight: 500,
  color: '#262626',
  fontSize: '14px',
  marginBottom: '8px',
};

const inputStyle: React.CSSProperties = {
  borderRadius: '6px',
  border: '1px solid #d9d9d9',
  padding: '8px 12px',
  fontSize: '14px',
};

export default CategoryForm;